import { useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useChatStore } from '../../enviroment/store/chatStore'
import {
  getLatestSession,
  createUserSession,
  sendProjectStreamingConversation,
  sendTenantStreamingConversation,
} from '../../enviroment/api/chat'
import { StreamCallbacks } from '../api/chat/models'

type UseChatSessionsParams = {
  userId: string
  tenantId: string
  projectId?: string
}

export function useChatSessions({ userId, tenantId, projectId }: UseChatSessionsParams) {
  const {
    loadSessionFromData,
    setIsLoadingSessions,
    setSessionId,
    addMessage,
    updateLastAssistantMessage,
    updateLastAssistantThinking,
    setLastAssistantThinkingDone,
    removeLastAssistantMessage,
    upsertMessageByType,
  } = useChatStore()

  const loadLatestSession = useCallback(async () => {
    if (!userId) return

    setIsLoadingSessions(true)
    try {
      const session = await getLatestSession({ userId })
      if (session && session.interactions.length > 0) {
        loadSessionFromData(session)
      }
    } catch (error) {
      console.error('Error loading latest session', error)
    } finally {
      setIsLoadingSessions(false)
    }
  }, [userId, loadSessionFromData, setIsLoadingSessions])

  /**
   * Creates a new session and streams COPA's welcome message into the chat.
   * The loading spinner is hidden as soon as the session ID arrives (onSessionInfo)
   * so the welcome message starts appearing immediately.
   * Resolves with the new session ID, or null if creation fails.
   */
  const createSessionWithWelcomeMessage = useCallback(
    async (abortSignal?: AbortSignal): Promise<string | null> => {
      const welcomeMessageId = uuidv4()

      addMessage({
        id: welcomeMessageId,
        role: 'assistant',
        content: '',
        date: new Date().toISOString(),
        isLoading: true,
        hideElapsed: true,
      })

      let sessionId: string | null = null

      try {
        const session = await createUserSession({ userId })
        sessionId = session.id
        setSessionId(sessionId)
      } catch (error) {
        console.error('Error creating session', error)
        removeLastAssistantMessage(welcomeMessageId)
        setIsLoadingSessions(false)
        return null
      }

      const callbacks: StreamCallbacks = {
        onChunk: (chunk) => updateLastAssistantMessage(chunk),
        onThinking: (step) => updateLastAssistantThinking(step),
        onThinkingDone: (durationMessage) => setLastAssistantThinkingDone(durationMessage),
        onSessionInfo: () => {
          setIsLoadingSessions(false)
        },
        onError: () => {
          removeLastAssistantMessage(welcomeMessageId)
          setIsLoadingSessions(false)
        },
      }

      try {
        if (projectId && sessionId) {
          await sendProjectStreamingConversation({ userId, tenantId, projectId, sessionId }, callbacks, abortSignal)
        } else if (sessionId) {
          await sendTenantStreamingConversation({ userId, tenantId, sessionId }, callbacks, abortSignal)
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('Error streaming welcome message', error)
        }
      }

      return sessionId
    },
    [
      userId,
      tenantId,
      projectId,
      setIsLoadingSessions,
      addMessage,
      updateLastAssistantMessage,
      updateLastAssistantThinking,
      setLastAssistantThinkingDone,
      removeLastAssistantMessage,
      setSessionId,
    ],
  )

  const createTenantSessionSilent = useCallback(async () => {
    try {
      const session = await createUserSession({ userId })
      setSessionId(session.id)
    } catch (error) {
      console.error('Error creating tenant session silently', error)
    }
  }, [userId, setSessionId])

  const createSessionWithMiomboWelcomeMessage = useCallback(async () => {
    const welcomeMessageId = uuidv4()

    try {
      addMessage({
        id: welcomeMessageId,
        role: 'assistant',
        content: '',
        date: new Date().toISOString(),
        isLoading: true,
        hideElapsed: true,
      })

      const session = await createUserSession({ userId })
      setSessionId(session.id)
      removeLastAssistantMessage(welcomeMessageId)

      upsertMessageByType('miombo-report')
    } catch (error) {
      console.error('Error creating tenant session silently', error)
      removeLastAssistantMessage(welcomeMessageId)
    }
  }, [userId, setSessionId])

  return {
    isLoadingSessions: useChatStore((s) => s.isLoadingSessions),
    loadLatestSession,
    createSessionWithWelcomeMessage,
    createSessionWithMiomboWelcomeMessage,
    createTenantSessionSilent,
  }
}
